import Button from '../common/Button/Button.component'
import { County } from '../../interface/CountyInterface'
import FemaleIcon from '@mui/icons-material/Female'
import InputField from '../common/InputField/InputField.component'
import Label from '../common/Label/Label'
import MaleIcon from '@mui/icons-material/Male'
import MaskedInput from 'react-maskedinput'
import PhoneConsentDisclaimer from '../common/PhoneConsentDisclaimer.component'
import RadioGroup from '../common/RadioGroup/RadioGroup.component'
import React from 'react'
import SectionRow from '../common/SectionRow'
import Select from '../common/Select/Select.component'
import { SelectChangeEvent } from '@mui/material'
import ValidationUtils from '../../utils/validation/ValidationUtils'
import YearMonthDaySelector from '../common/Select/YearMonthDaySelector'
import ZipUtils from '../../utils/ZipUtils'
import customerStore from '../../datastore/CustomerStore'
import { getPlanYear } from '../../utils/CommonUtils'
import { isEmpty } from 'lodash'
import { observer } from 'mobx-react'
import { pollQuotesPullStatus } from '../../utils/quotesPullStatus'
import snackbarStore from '../../datastore/SnackbarStore'
import styled from '@emotion/styled'
import theme from '../../global/theme'
import { updateCustomerDetails } from '../../service/customer'

interface DemographicsFormProps {
  onSuccess?: () => void
  onCancel?: () => void
  showEmail?: boolean
  showPhone?: boolean
  heading?: string
  subheaderText?: string
  extraActions?: React.ReactNode
  submitLabel?: string
  cancelLabel?: string
  contactOnly?: boolean // hides demographics fields; shows only email/phone
  submitId?: string // tracking id for the submit button, set per calling context
}

const FormHeader = styled.div`
  margin-bottom: 10px;
  h3 {
    font-size: 18px;
    color: ${theme.colors.secondary};
  }
  p {
    font-size: 14px;
    color: ${theme.colors.textGrey};
    margin-top: 4px;
  }
`

const FormActionsRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media screen and (max-width: 600px) {
    justify-content: space-between;
  }
`

const GenderToggle = styled.div`
  display: flex;
  width: 100%;
  border-radius: 50px;
  overflow: hidden;
  border: 1px solid ${theme.colors.primary};
  height: 42px;
  margin: 6px 0px 5px 0px;
`

const GenderButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  padding: 0 8px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: ${({ active }) => (active ? theme.colors.primary : '#fff')};
  color: ${({ active }) => (active ? '#fff' : theme.colors.textGrey)};
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    filter: brightness(0.9);
  }

  svg {
    font-size: 18px;
  }
`

const DemographicsForm: React.FC<DemographicsFormProps> = ({
  onSuccess,
  onCancel,
  showEmail = false,
  showPhone = false,
  heading = '',
  subheaderText = '',
  extraActions,
  submitLabel = 'Update Plans',
  cancelLabel = 'Cancel',
  contactOnly = false,
  submitId,
}) => {
  const parseDOB = (dob: string) => {
    if (!dob)
      return {
        year: '' as number | '',
        month: '' as number | '',
        day: '' as number | '',
      }
    const [y, m, d] = dob.split('-').map((val) => parseInt(val, 10))
    return {
      year: isNaN(y) ? ('' as const) : y,
      month: isNaN(m) ? ('' as const) : m,
      day: isNaN(d) ? ('' as const) : d,
    }
  }

  const formatDOB = (y: number | '', m: number | '', d: number | '') => {
    if (!y || !m || !d) return ''
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  }

  const [zip, setZip] = React.useState(customerStore.postalCode || '')
  const [city, setCity] = React.useState(customerStore.city || '')
  const [addrState, setAddrState] = React.useState(customerStore.state || '')
  const [optCounties, setOptCounties] = React.useState<County[]>(() => {
    if (customerStore.county && customerStore.countyFips) {
      return [
        {
          countyName: customerStore.county,
          countyFips: customerStore.countyFips,
        },
      ]
    }
    return []
  })
  const [county, setCounty] = React.useState<County>(() => ({
    countyName: customerStore.county || '',
    countyFips: customerStore.countyFips || '',
  }))
  const [year, setYear] = React.useState<number | ''>(
    () => parseDOB(customerStore.dateOfBirth).year,
  )
  const [month, setMonth] = React.useState<number | ''>(
    () => parseDOB(customerStore.dateOfBirth).month,
  )
  const [day, setDay] = React.useState<number | ''>(
    () => parseDOB(customerStore.dateOfBirth).day,
  )
  const [dobErrors, setDobErrors] = React.useState({
    year: '',
    month: '',
    day: '',
  })
  const [gender, setGender] = React.useState(() => {
    const g = customerStore.gender || ''
    return g ? g.charAt(0).toUpperCase() + g.slice(1).toLowerCase() : ''
  })
  const [hasSpouse, setHasSpouse] = React.useState<string>(() =>
    customerStore.spouseGender && customerStore.spouseDateOfBirth ? 'Yes' : '',
  )
  const [spouseGender, setSpouseGender] = React.useState(() => {
    const g = customerStore.spouseGender || ''
    return g ? g.charAt(0).toUpperCase() + g.slice(1).toLowerCase() : ''
  })
  const [spouseYear, setSpouseYear] = React.useState<number | ''>(
    () => parseDOB(customerStore.spouseDateOfBirth).year,
  )
  const [spouseMonth, setSpouseMonth] = React.useState<number | ''>(
    () => parseDOB(customerStore.spouseDateOfBirth).month,
  )
  const [spouseDay, setSpouseDay] = React.useState<number | ''>(
    () => parseDOB(customerStore.spouseDateOfBirth).day,
  )
  const [spouseDobErrors, setSpouseDobErrors] = React.useState({
    year: '',
    month: '',
    day: '',
  })
  const [zipLoading, setZipLoading] = React.useState(false)
  const [submitLoading, setSubmitLoading] = React.useState(false)
  const [zipError, setZipError] = React.useState(false)
  const [email, setEmail] = React.useState(
    showEmail ? customerStore.email || '' : '',
  )
  const [emailError, setEmailError] = React.useState('')
  const [phone, setPhone] = React.useState(
    showPhone ? customerStore.phone?.replace('+1', '') || '' : '',
  )
  const [phoneError, setPhoneError] = React.useState('')

  React.useEffect(() => {
    if (customerStore.postalCode && !zip) setZip(customerStore.postalCode)
    if (customerStore.dateOfBirth && !year && !month && !day) {
      const parsed = parseDOB(customerStore.dateOfBirth)
      setYear(parsed.year)
      setMonth(parsed.month)
      setDay(parsed.day)
    }
    if (customerStore.gender && !gender) {
      const g = customerStore.gender
      setGender(g.charAt(0).toUpperCase() + g.slice(1).toLowerCase())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customerStore.postalCode,
    customerStore.dateOfBirth,
    customerStore.gender,
  ])

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5)
    setZip(val)
    setZipError(false)
    if (val.length === 5) {
      setZipLoading(true)
      ZipUtils.GetCountyCityAndStateForZip(val)
        .then((data) => {
          setCity(data.city || '')
          setAddrState(data.state || '')
          if (!isEmpty(data.counties)) {
            setOptCounties(data.counties)
            setCounty(data.counties[0])
          } else {
            setOptCounties([])
            setCounty({ countyName: '', countyFips: '' })
            setZipError(true)
          }
        })
        .catch(() => {
          setZipError(true)
          setOptCounties([])
          setCounty({ countyName: '', countyFips: '' })
          setCity('')
          setAddrState('')
        })
        .finally(() => setZipLoading(false))
    } else {
      setCity('')
      setAddrState('')
      setOptCounties([])
      setCounty({ countyName: '', countyFips: '' })
    }
  }

  const handleCountyChange = (e: SelectChangeEvent<string>) => {
    const found = optCounties.find((c) => c.countyName === e.target.value)
    if (found) setCounty(found)
  }

  const validateDOBFields = (): boolean => {
    const newErrors = { year: '', month: '', day: '' }
    if (!year) newErrors.year = 'Required'
    if (!month) newErrors.month = 'Required'
    if (!day) newErrors.day = 'Required'
    if (
      year &&
      month &&
      day &&
      !ValidationUtils.validateDOB(formatDOB(year, month, day))
    ) {
      newErrors.day = 'Invalid date'
    }
    setDobErrors(newErrors)
    return Object.values(newErrors).every((error) => error === '')
  }

  const handleHasSpouseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setHasSpouse(val)
    if (val === 'Yes' && !spouseGender) {
      if (gender === 'Male') setSpouseGender('Female')
      else if (gender === 'Female') setSpouseGender('Male')
    }
  }

  const validateSpouseDOBFields = (): boolean => {
    const newErrors = { year: '', month: '', day: '' }
    if (!spouseYear) newErrors.year = 'Required'
    if (!spouseMonth) newErrors.month = 'Required'
    if (!spouseDay) newErrors.day = 'Required'
    if (
      spouseYear &&
      spouseMonth &&
      spouseDay &&
      !ValidationUtils.validateDOB(
        formatDOB(spouseYear, spouseMonth, spouseDay),
      )
    ) {
      newErrors.day = 'Invalid date'
    }
    setSpouseDobErrors(newErrors)
    return Object.values(newErrors).every((error) => error === '')
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setEmail(val)
    setEmailError(
      val.length > 0 && !ValidationUtils.validateEmail(val)
        ? 'Please enter a valid email address'
        : '',
    )
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[- )( _]/g, '')
    setPhone(val)
    setPhoneError(
      val.length > 0 && !ValidationUtils.validatePhoneNumber(val)
        ? 'Please enter a valid phone number'
        : '',
    )
  }

  // Validates the contact fields on submit so an empty required field surfaces
  // an inline error instead of silently blocking the button.
  const validateContactFields = (): boolean => {
    let emailMsg = ''
    let phoneMsg = ''
    if (showEmail) {
      if (!email) emailMsg = 'Email is required'
      else if (!ValidationUtils.validateEmail(email))
        emailMsg = 'Please enter a valid email address'
    }
    if (showPhone) {
      if (!phone) phoneMsg = 'Phone number is required'
      else if (!ValidationUtils.validatePhoneNumber(phone))
        phoneMsg = 'Please enter a valid phone number'
    }
    setEmailError(emailMsg)
    setPhoneError(phoneMsg)
    return !emailMsg && !phoneMsg
  }

  const currentDOB = formatDOB(year, month, day)
  const currentSpouseDOB = formatDOB(spouseYear, spouseMonth, spouseDay)
  const storedDOB = customerStore.dateOfBirth
    ? customerStore.dateOfBirth.substring(0, 10)
    : ''
  const storedSpouseDOB = customerStore.spouseDateOfBirth
    ? customerStore.spouseDateOfBirth.substring(0, 10)
    : ''

  const spouseDataUnchanged =
    (hasSpouse === 'Yes') === customerStore.spouseOptIn &&
    (hasSpouse !== 'Yes' ||
      (spouseGender.toLowerCase() === (customerStore.spouseGender || '') &&
        currentSpouseDOB === storedSpouseDOB))

  const isDataUnchanged =
    (contactOnly ||
      (zip === customerStore.postalCode &&
        county.countyName === customerStore.county &&
        addrState === customerStore.state &&
        city === customerStore.city &&
        gender.toLowerCase() === customerStore.gender &&
        currentDOB === storedDOB &&
        spouseDataUnchanged)) &&
    (!showEmail || email === (customerStore.email || '')) &&
    (!showPhone || phone === (customerStore.phone?.replace('+1', '') || ''))

  const isSubmitIncomplete =
    isDataUnchanged ||
    (showEmail && (!email || !!emailError)) ||
    (showPhone && (!phone || !!phoneError)) ||
    (!contactOnly &&
      (!zip ||
        zip.length < 5 ||
        zipError ||
        zipLoading ||
        isEmpty(county.countyName) ||
        !year ||
        !month ||
        !day ||
        !gender ||
        (hasSpouse === 'Yes' &&
          (!spouseGender || !spouseYear || !spouseMonth || !spouseDay))))

  // In contactOnly mode the button stays clickable so that submitting an
  // incomplete form surfaces inline validation errors instead of doing nothing.
  const isSubmitDisabled = submitLoading || (!contactOnly && isSubmitIncomplete)

  const handleSubmit = async () => {
    if (isSubmitDisabled) return
    if (contactOnly) {
      if (!validateContactFields()) return
      if (isDataUnchanged) {
        onSuccess?.()
        return
      }
    }
    if (!contactOnly && !validateDOBFields()) return
    if (!contactOnly && hasSpouse === 'Yes' && !validateSpouseDOBFields())
      return
    setSubmitLoading(true)

    const newDOB = currentDOB
    const dobChanged = !contactOnly && newDOB !== storedDOB
    const spouseGenderChanged =
      !contactOnly &&
      hasSpouse === 'Yes' &&
      spouseGender.toLowerCase() !== (customerStore.spouseGender || '')
    const spouseDobChanged =
      !contactOnly &&
      hasSpouse === 'Yes' &&
      currentSpouseDOB !== storedSpouseDOB
    const spouseOptInChanged =
      !contactOnly && (hasSpouse === 'Yes') !== customerStore.spouseOptIn
    const storePhone = customerStore.phone?.replace('+1', '') || ''

    const payload: Record<string, string | boolean> = {}
    if (zip !== customerStore.postalCode) payload.postalCode = zip
    if (addrState !== customerStore.state) payload.state = addrState
    if (city !== customerStore.city) payload.city = city
    if (
      county.countyName !== customerStore.county ||
      county.countyFips !== customerStore.countyFips
    ) {
      payload.county = county.countyName
      payload.countyFips = county.countyFips
    }
    if (gender.toLowerCase() !== customerStore.gender)
      payload.gender = gender.toLowerCase()
    if (dobChanged) payload.dateOfBirth = newDOB
    if (spouseGenderChanged) payload.spouseGender = spouseGender.toLowerCase()
    if (spouseDobChanged) payload.spouseDateOfBirth = currentSpouseDOB
    if (spouseOptInChanged) payload.spouseOptIn = hasSpouse === 'Yes'
    if (showEmail && email && email !== customerStore.email)
      payload.email = email
    if (showPhone && phone && phone !== storePhone) payload.phone = '+1' + phone

    try {
      await updateCustomerDetails(payload)
      if (payload.postalCode !== undefined) customerStore.postalCode = zip
      if (payload.state !== undefined) customerStore.state = addrState
      if (payload.city !== undefined) customerStore.city = city
      if (payload.county !== undefined) {
        customerStore.county = county.countyName
        customerStore.countyFips = county.countyFips
      }
      if (payload.gender !== undefined)
        customerStore.gender = gender.toLowerCase()
      if (dobChanged) customerStore.dateOfBirth = newDOB
      if (spouseGenderChanged)
        customerStore.spouseGender = spouseGender.toLowerCase()
      if (spouseDobChanged) customerStore.spouseDateOfBirth = currentSpouseDOB
      if (spouseOptInChanged) customerStore.spouseOptIn = hasSpouse === 'Yes'
      if (payload.email !== undefined) customerStore.email = email
      if (payload.phone !== undefined)
        customerStore.phone = payload.phone as string

      // Only a real demographics submission (zip/DOB/gender confirmed) should
      // unblur sample prices and trigger a quote refresh. A contactOnly
      // submission (email/phone only, e.g. the "Stay connected" popup) must
      // not unlock priced plans on its own.
      if (!contactOnly) {
        customerStore.setQuotePulledStatusMA(true)
        customerStore.setQuotePulledStatusMS(true)
        customerStore.setQuotePulledStatusMPartD(true)
        localStorage.removeItem('eip_sample_prices_banner')
        window.dispatchEvent(new Event('eip-demographics-updated'))
        snackbarStore.set({
          snackbarOpen: true,
          snackbarMessage: 'Profile updated. Refreshing plans...',
          snackbarType: 'success',
        })
        pollQuotesPullStatus(getPlanYear(), true)
      } else {
        snackbarStore.set({
          snackbarOpen: true,
          snackbarMessage: 'Contact details saved.',
          snackbarType: 'success',
        })
      }
      onSuccess?.()
    } catch {
      // Error snackbar already shown by updateCustomerDetails service
    } finally {
      setSubmitLoading(false)
    }
  }

  return (
    <>
      {(heading || subheaderText) && (
        <FormHeader>
          {heading && <h3>{heading}</h3>}
          {subheaderText && <p>{subheaderText}</p>}
        </FormHeader>
      )}

      {!contactOnly && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
            }}
          >
            <InputField
              fullWidth
              isRequired
              label='Zip Code'
              value={zip}
              onChange={handleZipChange}
              placeholder='55001'
              error={zipError}
              helperText={zipError ? 'Invalid ZIP' : ''}
              disabled={submitLoading}
            />
            <div>
              <Label required>Gender</Label>
              <GenderToggle>
                <GenderButton
                  active={gender === 'Male'}
                  onClick={() => setGender('Male')}
                  disabled={submitLoading}
                  type='button'
                >
                  <MaleIcon />
                  Male
                </GenderButton>
                <GenderButton
                  active={gender === 'Female'}
                  onClick={() => setGender('Female')}
                  disabled={submitLoading}
                  type='button'
                >
                  <FemaleIcon />
                  Female
                </GenderButton>
              </GenderToggle>
            </div>
          </div>
          {optCounties.length > 1 && (
            <SectionRow>
              <Select
                label='County'
                value={county.countyName}
                options={optCounties.map((c) => c.countyName)}
                onChange={handleCountyChange}
                bottomPadding={false}
                width='100%'
              />
            </SectionRow>
          )}
          <SectionRow style={{ display: 'none' }}>
            <InputField
              fullWidth
              label='City, State'
              value={
                city && addrState
                  ? `${city}, ${addrState}`
                  : addrState || city || ''
              }
              placeholder='—'
              readOnly
              disabled
            />
          </SectionRow>

          <SectionRow bottomPadding={false}>
            <YearMonthDaySelector
              label='Date of Birth'
              isRequired
              year={year}
              month={month}
              day={day}
              onYearChange={setYear}
              onMonthChange={setMonth}
              onDayChange={setDay}
              errors={dobErrors}
              setErrors={setDobErrors}
              loading={submitLoading}
            />
          </SectionRow>

          {!customerStore.spouseOptIn && (
            <RadioGroup
              label='Would you like to add a Spouse/Partner?'
              radioButtons={['Yes', 'No']}
              row
              value={hasSpouse}
              onChange={handleHasSpouseChange}
              radioGroupName='Has Spouse'
              disabled={submitLoading}
              bottomPadding={false}
              radioButtonStyle={{
                border: '1px solid rgba(0, 0, 0, 0.23)',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.87)',
                },
              }}
            />
          )}

          {!customerStore.spouseOptIn && hasSpouse === 'Yes' && (
            <div>
              <Label required>Spouse/Partner Gender</Label>
              <GenderToggle>
                <GenderButton
                  active={spouseGender === 'Male'}
                  onClick={() => setSpouseGender('Male')}
                  disabled={submitLoading}
                  type='button'
                >
                  <MaleIcon />
                  Male
                </GenderButton>
                <GenderButton
                  active={spouseGender === 'Female'}
                  onClick={() => setSpouseGender('Female')}
                  disabled={submitLoading}
                  type='button'
                >
                  <FemaleIcon />
                  Female
                </GenderButton>
              </GenderToggle>
            </div>
          )}

          {!customerStore.spouseOptIn && hasSpouse === 'Yes' && (
            <SectionRow bottomPadding={false}>
              <YearMonthDaySelector
                label='Spouse/Partner Date of Birth'
                isRequired
                year={spouseYear}
                month={spouseMonth}
                day={spouseDay}
                onYearChange={setSpouseYear}
                onMonthChange={setSpouseMonth}
                onDayChange={setSpouseDay}
                errors={spouseDobErrors}
                setErrors={setSpouseDobErrors}
                loading={submitLoading}
              />
            </SectionRow>
          )}
        </>
      )}

      {(showEmail || showPhone) && (
        <FormHeader>
          <p>
            {showEmail
              ? `Don't lose your progress! Enter your email below and we'll send you a link to pick up where you left off, along with updates when your premiums change.`
              : `Share your phone number so we can reach out to help you with your plan options.`}
          </p>
        </FormHeader>
      )}

      {showEmail && (
        <SectionRow bottomPadding={false}>
          <InputField
            placeholder='john@doe.com'
            fullWidth
            isRequired
            label='Email'
            value={email}
            onChange={handleEmailChange}
            disabled={submitLoading}
            error={!!emailError}
            helperText={emailError}
          />
        </SectionRow>
      )}

      {showPhone && (
        <SectionRow bottomPadding={false}>
          <InputField
            fullWidth
            isRequired
            label='Phone'
            value={phone}
            placeholder='(123) 456-7890'
            onChange={handlePhoneChange}
            disabled={submitLoading}
            inputComponent={MaskedInput}
            inputProps={{ mask: '(111) 111-1111' }}
            shrink={phone ? true : undefined}
            error={!!phoneError}
            helperText={phoneError}
          />
        </SectionRow>
      )}

      <FormActionsRow>
        {onCancel && (
          <Button
            variant='outlined'
            color='primary'
            bold={false}
            fontSize='13px'
            height='40px'
            width='100%'
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
        )}
        <Button
          variant='contained'
          color='secondary'
          bold={false}
          fontSize='13px'
          height='40px'
          width='100%'
          onClick={handleSubmit}
          loading={submitLoading || zipLoading}
          disabled={isSubmitDisabled}
          description='Update plans with confirmed demographics'
          id={submitId}
        >
          {submitLabel}
        </Button>
        {extraActions}
      </FormActionsRow>

      {showPhone && !customerStore.phone && (
        <PhoneConsentDisclaimer buttonLabel={submitLabel} />
      )}
    </>
  )
}

export default observer(DemographicsForm)
